import { useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';
import { getDefaultCurrency, STORAGE_KEYS } from '@/store/balance';
import { useGetGameUrl } from '../services/game';
import { rummyPreviewUrlAtom } from '../store';
import type { RummyGameUrlReq } from '@/types/api/hall/rummy-game-url.d';

interface UsePrefetchRummyGameUrlOptions {
  /**
   * 延迟时间（毫秒），默认 500ms
   */
  delay?: number;
  is_preview?: boolean;
  /**
   * 请求成功后的回调
   */
  onSuccess?: (data: { game_url?: string; }) => void;
  /**
   * 请求失败后的回调
   */
  onError?: (error: Error) => void;
}

/**
 * 预获取 Rummy 游戏 URL 的 Hook
 * 
 * @example
 * ```tsx
 * const prefetchRummyGameUrl = usePrefetchRummyGameUrl({
 *   delay: 500,
 *   onSuccess: (data) => {
 *     console.log('Game URL:', data.game_url);
 *   },
 * });
 * 
 * // 调用
 * prefetchRummyGameUrl();
 * ```
 */
export function usePrefetchRummyGameUrl(options: UsePrefetchRummyGameUrlOptions = {}) {
  const { delay = 500, is_preview = false, onSuccess, onError } = options;
  const setRummyPreviewUrl = useSetAtom(rummyPreviewUrlAtom);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { mutate: httpGameUrl } = useGetGameUrl({
    mutationConfig: {
      onSuccess: (data) => {
        // 无论 game_url 是否存在，都更新到 rummyGameUrlAtom
        setRummyPreviewUrl(data?.game_url || '');
        onSuccess?.(data);
      },
      onError: (error) => {
        onError?.(error);
      },
    },
  });

  const prefetchRummyGameUrl = useCallback(
    (params?: Partial<RummyGameUrlReq>) => {
      // 清除之前的定时器
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        // 从 localStorage 获取货币
        let curCurrency = '';
        if (localStorage.getItem(STORAGE_KEYS.SELECTED_CURRENCY)) {
          curCurrency = JSON.parse(localStorage.getItem(STORAGE_KEYS.SELECTED_CURRENCY) || '{}') || '';
        }

        httpGameUrl({
          params: {
            // 预览模式下，isPreview 为 1
            is_preview: is_preview ? 1 : 0,
            currency: params?.currency || curCurrency || getDefaultCurrency(),
            device_type: params?.device_type ?? 1, // 1: H5, 2: Web
            return_url: params?.return_url || location.href,
            game_id: params?.game_id,
            match_config_id: params?.match_config_id,
            level: params?.level,
            seat_count: params?.seat_count,
          },
        });
      }, delay);
    },
    [delay, httpGameUrl]
  );

  // 组件卸载时清理定时器
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return prefetchRummyGameUrl;
}

